import React, { useState } from 'react';
import { Alert, Box, Link, Paper, Typography } from '@mui/material';
import type { ProductProgramMatch, ProductSourceResults } from '../api/stageFunctions';
import { isCatalogApiLink, openCatalogSourcePdf, productSourceLink } from '../utils/productSourceLink';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

/** Program documents are research evidence, never product rows or quotations. */
export default function ProductProgramEvidence({ results, quoteProducts = [] }: {
  results: ProductSourceResults | null; quoteProducts?: unknown[];
}) {
  const [linkError, setLinkError] = useState<string | null>(null);
  const checks = (Array.isArray(results?.items) ? results!.items : [])
    .filter(item => item && typeof item.item_id === 'string')
    .map(item => ({ itemId: item.item_id, check: item.checks?.carpet_ii }))
    .filter(({ check }) => check && ((Array.isArray(check.program_matches) && check.program_matches.length)
      || (Array.isArray(check.contract_matches) && check.contract_matches.length)));
  const products = (Array.isArray(quoteProducts) ? quoteProducts : []).filter((product): product is Record<string, unknown> =>
    !!product && typeof product === 'object' && typeof (product as Record<string, unknown>).product_name === 'string');
  if (!checks.length && !products.length) return null;

  const row = (match: ProductProgramMatch, index: number, kind: string) => {
    const href = productSourceLink({ evidence: {
      source: 'carpet_ii', doc_type: 'catalog_pdf',
      document_id: match.evidence?.document_id ?? match.document_id,
      catalog_id: match.catalog_id,
      page: match.evidence?.page ?? match.catalog_page,
      url: match.evidence?.url ?? match.official_url,
    } }, API_BASE_URL);
    return <Box key={`${kind}-${index}`} component="li" sx={{ mb: 1 }}>
      <Typography variant="body2">
        {href ? <Link href={href} target="_blank" rel="noopener noreferrer" onClick={event => {
          if (!isCatalogApiLink(href, API_BASE_URL)) return;
          event.preventDefault();
          setLinkError(null);
          void openCatalogSourcePdf(href).catch(() => setLinkError('Unable to open the catalog. Please sign in again or retry.'));
        }}>{match.title}</Link> : match.title}
        {(match.supplier ?? match.supplier_name) && ` — ${match.supplier ?? match.supplier_name}`}
        {match.contract_number && ` (${match.contract_number})`}
      </Typography>
      {match.scope_note && <Typography variant="caption" color="text.secondary">{match.scope_note}</Typography>}
    </Box>;
  };

  return <Paper component="section" aria-label="Carpet II program and contract evidence" variant="outlined" sx={{ p: 2, mb: 2 }}>
    <Typography variant="h6" component="h2">Carpet II program and contract evidence</Typography>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
      These documents identify the ordering process and suppliers; they are not quotations or an approved ordering decision.
      Your contracting team must verify current contract terms and applicability.
    </Typography>
    {checks.map(({ itemId, check }) => <Box key={itemId}>
      {check.coverage_note && <Typography variant="body2" color="text.secondary">{check.coverage_note}</Typography>}
      <Box component="ul" sx={{ pl: 3, mt: 1, mb: 0 }}>
        {(Array.isArray(check.program_matches) ? check.program_matches : [])
          .filter(match => match && typeof match.title === 'string').map((match, index) => row(match, index, 'program'))}
        {(Array.isArray(check.contract_matches) ? check.contract_matches : [])
          .filter(match => match && typeof match.title === 'string').map((match, index) => row(match, index, 'contract'))}
      </Box>
    </Box>)}
    {products.length > 0 && <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle2">Catalog products requiring a quote</Typography>
      <Typography variant="body2" color="text.secondary">Request a current supplier quote. Catalog matches have no verified ordering price and cannot be selected as priced listings.</Typography>
      <Box component="ul" sx={{ pl: 3, mt: 1, mb: 0 }}>{products.map((product, index) => {
        const evidence = product.evidence && typeof product.evidence === 'object' ? product.evidence as Record<string, unknown> : {};
        return row({ title: [product.product_name, product.model, product.manufacturer]
          .filter(value => typeof value === 'string' && value).join(' — '),
          document_id: typeof evidence.document_id === 'string' ? evidence.document_id : undefined,
          catalog_page: typeof evidence.page === 'number' ? evidence.page : undefined,
          official_url: typeof evidence.url === 'string' ? evidence.url : undefined,
        }, index, 'quote-product');
      })}</Box>
    </Box>}
    {linkError && <Alert severity="error" sx={{ mt: 1 }}>{linkError}</Alert>}
  </Paper>;
}
